import React, { useMemo, useState, useCallback } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { translate } from '@utils/i18n';
import FilterPanel from '@components/FilterPanel';
import ColumnMenu from '@components/Kendo/ColumnMenu';
import ColumnMenuContext from '@components/Kendo/ColumnMenuContext';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import SearchPanel from '@components/SearchPanel';
import { Button } from '@ui';
import { COLUMNS_DATA, INITIAL_FILTERS, NAMESPACE, ALL_COLUMNS, LOCATION_COLUMNS } from './constants';
import './SprinklerSamplingIndex.scss';
import CustomScrollbar from '../../components/CustomScrollbar/CustomScrollbar';
import SprinklerSampleSubmissionForm from './SprinklerSampleSubmissionForm';
import useFilters from './hooks/useFilters';
import { actionTypes } from './reducer';
import ContactDyneModal from './components/ContactDyneModal';
import KitOrderForm from './components/KitOrderForm';
import useFilteredSprinklerSampling from './hooks/useFilteredSprinklerSampling';
function SprinklerSamplingTable({ buildingId, showActions = true }) {
    const [isBrushawayOpen, setIsBrushawayOpen] = useState(false);
    const [isContactDyneOpen, setIsContactDyneOpen] = useState(false);
    const [isKitOrderOpen, setIsKitOrderOpen] = useState(false);
    const { data, totalCount, isLoading, error, page, sort, filters, columns, dispatch, onResetCache } = useFilteredSprinklerSampling({ buildingId });
    const { filtersList, onSearchChange } = useFilters({ filters, dispatch });
    const allMenuColumns = useMemo(() => {
        const ids = buildingId === undefined ? [...LOCATION_COLUMNS, ...ALL_COLUMNS] : ALL_COLUMNS;
        return ids.map((columnId) => COLUMNS_DATA[columnId]);
    }, [buildingId]);
    const visibleColumns = useMemo(() => {
        const ids = buildingId === undefined ? [...LOCATION_COLUMNS, ...columns] : columns;
        return ids.map((columnId) => COLUMNS_DATA[columnId]);
    }, [columns, buildingId]);
    const onColumnsChange = useCallback((cols) => {
        const selectedColumns = cols.map((col) => col.id).filter((id) => Boolean(id));
        dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: selectedColumns });
    }, [dispatch]);
    const columnContextValue = useMemo(() => ({
        onColumnsChange,
        columnsState: visibleColumns,
        columns: allMenuColumns,
        dispatch
    }), [onColumnsChange, visibleColumns, allMenuColumns, dispatch]);
    const onResetFilters = () => {
        dispatch({ type: actionTypes.FILTER_CHANGED, filters: INITIAL_FILTERS });
    };
    return (React.createElement("div", { className: "sprinkler-sampling-container" },
        showActions && (React.createElement("div", { className: "sprinkler-sampling-header" },
            React.createElement("p", null,
                translate('header_text', { namespace: NAMESPACE }),
                ' ',
                React.createElement("strong", null, translate('results_turnaround', { namespace: NAMESPACE }))))),
        showActions && (React.createElement("div", { className: "qmb-content-actions" },
            React.createElement("div", { className: "content-actions__group" },
                React.createElement(Button, { onClick: () => {
                        setIsKitOrderOpen(false);
                        setIsBrushawayOpen(true);
                    } },
                    React.createElement("i", { className: "fa-light fa-circle-plus" }),
                    translate('new_submission', { namespace: NAMESPACE }))),
            React.createElement("div", { className: "content-actions__group--separate" },
                React.createElement(Button, { onClick: () => {
                        setIsBrushawayOpen(false);
                        setIsKitOrderOpen(true);
                    } }, translate('order_kits', { namespace: NAMESPACE }))),
            React.createElement("div", { className: "content-actions__group--separate" },
                React.createElement(Button, { onClick: () => setIsContactDyneOpen(true) }, translate('contact_dyne', { namespace: NAMESPACE }))))),
        showActions && React.createElement("hr", { className: "workspace__split" }),
        React.createElement(FilterPanel, { onResetFilters: onResetFilters, onResetCache: onResetCache, filters: filtersList },
            React.createElement("div", { className: "filters__group--search" },
                React.createElement(SearchPanel, { placeholder: translate('search', { namespace: 'filters' }), onChange: onSearchChange }))),
        error && (React.createElement("div", { className: "workspace__section" },
            React.createElement("div", { className: "qmb-alert--error" },
                React.createElement("i", { className: "fa-light fa-exclamation-triangle alert__visual" }),
                error.message || translate('error', { namespace: 'generic' })))),
        isLoading && (React.createElement("div", { className: "workspace__section" },
            React.createElement("div", { className: "qmb-loading--96", "data-title": translate('loading', { namespace: 'generic' }) },
                React.createElement("svg", { role: "img" },
                    React.createElement("use", { href: "/map.svg#load-spinner" }))))),
        !isLoading && !error && data && (React.createElement(ColumnMenuContext.Provider, { value: columnContextValue },
            React.createElement(CustomScrollbar, null,
                React.createElement(Grid, { className: "qmb-grid--01", data: data, dataItemKey: "id", total: totalCount, skip: page.skip, take: page.take, onPageChange: (event) => {
                        dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
                    }, pageable: {
                        type: 'input',
                        pageSizes: [10, 25, 50],
                        pageSizeValue: page.take
                    }, pager: EnterKeyPager, sortable: true, sort: sort, onSortChange: (event) => {
                        dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
                    } }, visibleColumns.map((column) => (React.createElement(Column, { key: column.id, field: column.field, title: column.title, columnMenu: ColumnMenu, cell: column.cell, sortable: column.sortable, width: column.width }))))))),
        isContactDyneOpen && React.createElement(ContactDyneModal, { onClose: () => setIsContactDyneOpen(false) }),
        showActions && buildingId !== undefined && (React.createElement(React.Fragment, null,
            React.createElement(SprinklerSampleSubmissionForm, { buildingId: buildingId, isOpen: isBrushawayOpen, onClose: () => setIsBrushawayOpen(false) }),
            React.createElement(KitOrderForm, { buildingId: buildingId, isOpen: isKitOrderOpen, onClose: () => setIsKitOrderOpen(false) })))));
}
export default SprinklerSamplingTable;
